/**
 * Les petits mots du temps, prêts à poser dans une phrase, une cellule, une carte :
 *
 *   <Heure instant={ms} fuseau="Europe/Paris" avecFuseau />          09:42 PARIS
 *   <Heure instant={ms} fuseau="Europe/Paris" format="jourHeure" />  lun. 14 juil. · 09:42
 *   <Duree ms={9_900_000} />                                          2 h 45 min
 *   <TempsRelatif instant={ms} fuseau="Europe/Paris" />               il y a 12 min (vivant)
 *
 * Une heure s'écrit dans un fuseau NOMMÉ, jamais dans celui du poste ; `avecFuseau` l'écrit à côté
 * (la règle de la famille : l'équipe est à Paris, Kayla en UTC−3). Une valeur absente ne s'écrit
 * pas : ni « 0 », ni « — » — c'est à l'écran de dire ce que vaut un vide.
 */
import type { ReactNode } from "react";

import { Infobulle } from "../Infobulle";
import { cn } from "../utils";
import { formatDuree, formatHeure, formatJour, formatRelatif, type OptionsDuree } from "./format";
import { libelleDuFuseau } from "./fuseaux";
import { useMaintenant } from "./useMaintenant";
import "../champs.css";
import "./temps.css";

export type FormatHeure = "heure" | "jour" | "jourHeure" | "long";

export interface HeureProps {
  /** L'instant (ms epoch). `null` : rien ne s'écrit. */
  instant: number | null | undefined;
  /** Le fuseau dans lequel on lit l'horloge (« Europe/Paris »). */
  fuseau: string;
  /** `heure` 09:42 (défaut) · `jour` lun. 14 juil. · `jourHeure` lun. 14 juil. · 09:42 · `long` lundi 14 juillet · 09:42. */
  format?: FormatHeure;
  /** Écrire le fuseau à côté : `true` = sa ville (« Paris »), une chaîne = ce texte (« heure de Paris »). */
  avecFuseau?: boolean | string;
  locale?: string;
  className?: string;
}

/** Le texte d'un instant dans un format — ce que `Heure` écrit. */
export function texteDeLHeure(instant: number, fuseau: string, format: FormatHeure = "heure", locale = "fr"): string {
  const h = formatHeure(instant, fuseau, locale);
  if (format === "heure") return h;
  if (format === "jour") return formatJour(instant, fuseau, "court", locale);
  return `${formatJour(instant, fuseau, format === "long" ? "long" : "court", locale)} · ${h}`;
}

export function Heure({ instant, fuseau, format = "heure", avecFuseau = false, locale = "fr", className }: HeureProps) {
  if (instant === null || instant === undefined || !Number.isFinite(instant)) return null;
  const fus = avecFuseau === true ? libelleDuFuseau(fuseau) : avecFuseau || null;
  return (
    <time dateTime={new Date(instant).toISOString()} className={cn("tp-chiffres whitespace-nowrap", className)}>
      <span className={format !== "heure" ? "tp-majuscule inline-block" : undefined}>{texteDeLHeure(instant, fuseau, format, locale)}</span>
      {fus && <span className="ch-etiquette tp-fuseau">{fus}</span>}
    </time>
  );
}

export interface DureeProps extends OptionsDuree {
  /** La durée en millisecondes… */
  ms?: number | null;
  /** … ou en minutes (un créneau, un total d'heures). */
  minutes?: number | null;
  className?: string;
}

export function Duree({ ms, minutes, className, ...options }: DureeProps) {
  const valeur = ms ?? (minutes === null || minutes === undefined ? null : minutes * 60_000);
  if (valeur === null || !Number.isFinite(valeur)) return null;
  return <span className={cn("tp-chiffres whitespace-nowrap", className)}>{formatDuree(valeur, options)}</span>;
}

export interface TempsRelatifProps {
  /** L'instant dont on dit l'âge. `null` : rien ne s'écrit. */
  instant: number | null | undefined;
  /** Donné : l'infobulle dit l'instant exact dans ce fuseau (« lun. 14 juil. · 09:42 Paris »). */
  fuseau?: string;
  /** Un « maintenant » figé (une démo, un test, une app qui a déjà son tic). Sans : il avance tout seul. */
  maintenant?: number;
  locale?: string;
  /** Avant le texte (« Vu »). */
  avant?: ReactNode;
  className?: string;
}

/**
 * « il y a 12 min », qui vieillit tout seul (une minuterie pour toute la page, `useMaintenant`).
 * Remplace les cinq `il y a ${…}` écrits à la main de Helios et ceux du cockpit.
 */
export function TempsRelatif({ instant, fuseau, maintenant: fige, locale = "fr", avant, className }: TempsRelatifProps) {
  const maintenant = useMaintenant(30_000, fige);
  if (instant === null || instant === undefined || !Number.isFinite(instant)) return null;
  const texte = (
    <time dateTime={new Date(instant).toISOString()} className={cn("whitespace-nowrap", className)}>
      {avant !== undefined && <>{avant} </>}
      {formatRelatif(instant, maintenant, locale)}
    </time>
  );
  if (!fuseau) return texte;
  return <Infobulle texte={`${texteDeLHeure(instant, fuseau, "jourHeure", locale)} ${libelleDuFuseau(fuseau)}`}>{texte}</Infobulle>;
}
